import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  ToggleButton,
  ToggleButtonGroup,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  ClientSummary,
  CommissionMode,
  CommissionTier,
  CreateDistributorRequest,
  Distributor,
  DistributorClient,
} from '../api/types'
import { useOpsApi } from '../api/context'
import { useCurrentUser } from '../auth'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'
import { SectionCard } from '../components/SectionCard'
import { StatementButtons } from '../components/StatementButtons'

/**
 * Správa distributorů: evidence, založení (jmenná role + volitelný login),
 * přidělení klientů (client_role) a notifikace o událostech jejich klientů.
 * Sazby MF/SF se NEeditují zde — jsou per fond × třída (Fee → Sazby).
 */
export function DistributoriPhase() {
  const api = useOpsApi()
  const [distributors, setDistributors] = useState<Distributor[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [clientsFor, setClientsFor] = useState<Distributor | null>(null)
  const [editFor, setEditFor] = useState<Distributor | null>(null)
  const [statementFor, setStatementFor] = useState<Distributor | null>(null)
  const [credentials, setCredentials] = useState<Distributor | null>(null)
  const user = useCurrentUser()

  const reload = useCallback(() => {
    api
      .listDistributorsDetail()
      .then(setDistributors)
      .catch(() => setError('Distributory se nepodařilo načíst.'))
  }, [api])

  useEffect(() => {
    reload()
  }, [reload])

  const handleLogin = async (d: Distributor) => {
    const reset = !!d.login
    const question = reset
      ? `Vygenerovat nové dočasné heslo pro ${d.login}? Staré heslo přestane platit.`
      : `Založit přihlašovací účet pro ${d.name} (login ${d.email})? Přihlašovací údaje se zobrazí jen jednou.`
    if (!window.confirm(question)) return
    try {
      const res = reset ? await api.resetDistributorPassword(d.id) : await api.createDistributorLogin(d.id)
      setCredentials(res)
      reload()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Přihlašovací údaje se nepodařilo vygenerovat.')
    }
  }

  const handleToggle = async (d: Distributor, field: 'active' | 'notifyClientEvents', value: boolean) => {
    try {
      await api.updateDistributor(d.id, { [field]: value })
      reload()
    } catch {
      setError('Uložení se nepodařilo.')
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Distributoři"
        subtitle="Evidence obsluhy, zakládání účtů, přidělení klientů a notifikace."
        info={
          <InfoHint
            title="Jak distribuce funguje"
            lines={[
              'Distributor = jmenná role v Portiliu; klient mu patří přiřazením této role (obsluha).',
              'Login účet má roli DISTRIBUTOR — read-only přístup: vidí jen své klienty, jejich dokumenty a svůj Management fee report.',
              'Přihlašovací údaje se distributorovi NEPOSÍLAJÍ automaticky: aplikace je jednou zobrazí a obsluha je předá sama.',
              'Klient má jediného distributora — přiřazením k jinému se předchozímu odebere.',
              'Klientovi se strhne sazba dle třídy (A/B) a fondu (Fee → Sazby); distributor dostane svůj podíl — pevný, nebo podle objemového pásma.',
              'Notifikace: distributor s e-mailem a zapnutým zvonkem dostane kopii všech e-mailů svým klientům (vklad, výběr, pokyn, jednotky).',
            ]}
          />
        }
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            Nový distributor
          </Button>
        }
      />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {snackbar && <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>}

      <SectionCard
        title="Evidence distributorů"
        info={
          <InfoHint
            title="Sloupce"
            lines={[
              'Klientů = počet klientů s jmennou rolí distributora (klik zobrazí seznam a umožní přidat/odebrat).',
              'Login = přihlašovací účet do aplikace (jen CEO: založit účet / nové heslo).',
              'Notifikace = kopie všech e-mailů jeho klientům. Víc adres v e-mailu oddělte čárkou.',
              'Neaktivní distributor zůstává v evidenci a reportech, ale neměl by dostávat nové klienty.',
            ]}
            sx={{ color: '#fff' }}
          />
        }
      >
        {!distributors ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Název</TableCell>
                  <TableCell>Jmenná role</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell align="center">Klientů</TableCell>
                  <TableCell>Login</TableCell>
                  <TableCell align="right">
                    Podíl MF{' '}
                    <InfoHint
                      lines={[
                        'Nasmlouvaný podíl distributora ze STRŽENÉHO management fee jeho klientů (např. 50 %).',
                        'Fond klientovi strhne plnou sazbu dle třídy (A/B); z ní se podíl rozdělí: distributor × podíl, zbytek fond.',
                        'Prázdné = distributor z MF nedostává nic.',
                      ]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    Podíl SF{' '}
                    <InfoHint
                      lines={[
                        'Nasmlouvaný podíl ze strženého success fee jeho klientů.',
                        'Prázdné = použije se výchozí distribuční sazba fondu.',
                      ]}
                    />
                  </TableCell>
                  <TableCell align="center">Notifikace</TableCell>
                  <TableCell align="center">Aktivní</TableCell>
                  <TableCell align="center">Upravit</TableCell>
                  <TableCell align="center">Vyúčtování</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributors.map((d) => (
                  <TableRow key={d.id} sx={{ opacity: d.active === false ? 0.55 : 1 }}>
                    <TableCell sx={{ fontWeight: 600 }}>{d.name}</TableCell>
                    <TableCell>
                      {d.roleName ?? <Chip size="small" label="interní" variant="outlined" />}
                    </TableCell>
                    <TableCell>{d.email || '—'}</TableCell>
                    <TableCell align="center">
                      {d.roleId ? (
                        <Button
                          size="small"
                          startIcon={<GroupOutlinedIcon />}
                          onClick={() => setClientsFor(d)}
                        >
                          {d.clientCount ?? 0}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {d.login ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{d.login}</span>
                          {user.isCeo && (
                            <Button size="small" onClick={() => handleLogin(d)}>
                              Nové heslo
                            </Button>
                          )}
                        </Box>
                      ) : user.isCeo && d.roleId ? (
                        <Tooltip title={d.email ? 'Vygenerovat přihlašovací údaje' : 'Nejdřív doplňte e-mail (bude loginem)'}>
                          <span>
                            <Button size="small" onClick={() => handleLogin(d)} disabled={!d.email}>
                              Založit účet
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <RateButton d={d} kind="mf" onClick={() => setEditFor(d)} />
                    </TableCell>
                    <TableCell align="right">
                      <RateButton d={d} kind="sf" onClick={() => setEditFor(d)} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Posílat kopii všech e-mailů jeho klientům (vyžaduje e-mail)">
                        <span>
                          <Switch
                            size="small"
                            checked={!!d.notifyClientEvents}
                            disabled={!d.email}
                            onChange={(e) => handleToggle(d, 'notifyClientEvents', e.target.checked)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={d.active !== false}
                        disabled={!d.roleId}
                        onChange={(e) => handleToggle(d, 'active', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => setEditFor(d)} disabled={!d.roleId}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => setStatementFor(d)}>
                        Vyúčtování
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Klientovi se vždy strhne plná sazba dle třídy (A/B) a fondu (Fee → Sazby fee). Podíl MF/SF
          říká, kolik ze stržené částky dostane distributor — zbytek zůstává fondu.
        </Typography>
      </SectionCard>

      {statementFor && (
        <DistributorStatementDialog distributor={statementFor} onClose={() => setStatementFor(null)} />
      )}

      <NewDistributorDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        canCreateLogin={user.isCeo}
        onCreated={(d) => {
          setNewOpen(false)
          setSnackbar('Distributor ' + d.name + ' založen (role ' + (d.roleName ?? '') + ').')
          if (d.generatedPassword) setCredentials(d)
          reload()
        }}
      />

      {credentials && <CredentialsDialog distributor={credentials} onClose={() => setCredentials(null)} />}

      {editFor && (
        <EditDistributorDialog
          distributor={editFor}
          onClose={() => setEditFor(null)}
          onSaved={() => {
            setEditFor(null)
            setSnackbar('Uloženo.')
            reload()
          }}
        />
      )}

      {clientsFor && (
        <DistributorClientsDialog
          distributor={clientsFor}
          onClose={() => {
            setClientsFor(null)
            reload()
          }}
        />
      )}
    </Box>
  )
}

/** Jednorázové zobrazení přihlašovacích údajů distributora (nic se neposílá e-mailem). */
function CredentialsDialog({ distributor, onClose }: { distributor: Distributor; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const text =
    `Přístup distributora do Portilia (jen pro čtení: vaši klienti, management fee, vyúčtování)\n` +
    `Adresa: ${window.location.origin}\n` +
    `Přihlašovací jméno: ${distributor.login}\n` +
    `Dočasné heslo: ${distributor.generatedPassword}\n` +
    `Při prvním přihlášení si heslo změníte.`
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Přihlašovací údaje distributora — {distributor.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Alert severity="warning">
          Heslo se zobrazí jen teď a nikde se neukládá. Předejte ho distributorovi sami. Je to distributorský
          účet (jen pro čtení), ne klientský.
        </Alert>
        <TextField size="small" label="Přihlašovací jméno" value={distributor.login ?? ''} InputProps={{ readOnly: true }} />
        <TextField
          size="small"
          label="Dočasné heslo (při prvním přihlášení se mění)"
          value={distributor.generatedPassword ?? ''}
          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy}>
            Zkopírovat text pro distributora
          </Button>
          {copied && <Typography variant="body2" color="success.main">Zkopírováno</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Hotovo
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function NewDistributorDialog({
  open,
  onClose,
  onCreated,
  canCreateLogin,
}: {
  open: boolean
  onClose: () => void
  onCreated: (d: Distributor) => void
  canCreateLogin: boolean
}) {
  const api = useOpsApi()
  const [form, setForm] = useState<CreateDistributorRequest>({ name: '', createLogin: false, notifyClientEvents: false })
  const [mfPct, setMfPct] = useState('')
  const [sfPct, setSfPct] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setForm({ name: '', email: '', firstName: '', lastName: '', createLogin: false, notifyClientEvents: false })
      setMfPct('')
      setSfPct('')
      setError(null)
    }
  }, [open])

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError('Název je povinný.')
      return
    }
    if (form.createLogin && !(form.email || '').trim()) {
      setError('Pro přihlašovací účet je e-mail povinný.')
      return
    }
    const toRate = (value: string): number | undefined | null => {
      if (!value.trim()) return undefined
      const n = Number(value.replace(',', '.'))
      return Number.isNaN(n) || n < 0 || n > 100 ? null : n / 100
    }
    const mf = toRate(mfPct)
    const sf = toRate(sfPct)
    if (mf === null || sf === null) {
      setError('Podíl zadejte v procentech 0–100.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const created = await api.createDistributor({
        ...form,
        name: form.name.trim(),
        email: (form.email || '').trim() || undefined,
        mfCommissionRate: mf,
        sfCommissionRate: sf,
      })
      onCreated(created)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Založení se nepodařilo.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Nový distributor{' '}
        <InfoHint
          title="Co se založí"
          lines={[
            'Jmenná role v Portiliu PRIJMENI_JMENO (např. NOVAK_JAN) — přes ni se distributorovi přidělují klienti.',
            'Záznam v evidenci distributorů (rozpad fee reportů).',
            'Volitelně přihlašovací účet (jen CEO): role DISTRIBUTOR (read-only). Aplikace jednou zobrazí login a dočasné heslo, nic se neposílá.',
            'Login = e-mail. Je-li e-mail už použitý jiným účtem (např. klientským), použijte jiný, třeba jmeno+distributor@gmail.com.',
          ]}
        />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          size="small"
          label="Název (firma / jméno) *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            sx={{ flexGrow: 1 }}
            label="Jméno (pro účet)"
            value={form.firstName || ''}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <TextField
            size="small"
            sx={{ flexGrow: 1 }}
            label="Příjmení (pro účet)"
            value={form.lastName || ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </Box>
        <TextField
          size="small"
          label={form.createLogin ? 'E-mail (login) *' : 'E-mail'}
          type="email"
          value={form.email || ''}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            sx={{ flexGrow: 1 }}
            label="Podíl z MF (%)"
            placeholder="např. 50"
            value={mfPct}
            onChange={(e) => setMfPct(e.target.value)}
          />
          <TextField
            size="small"
            sx={{ flexGrow: 1 }}
            label="Podíl ze SF (%)"
            placeholder="prázdné = dle fondu"
            value={sfPct}
            onChange={(e) => setSfPct(e.target.value)}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.createLogin}
              disabled={!canCreateLogin}
              onChange={(e) => setForm({ ...form, createLogin: e.target.checked })}
            />
          }
          label={
            'Vygenerovat přihlašovací údaje (zobrazí se jednou, předáte je sami)' +
            (canCreateLogin ? '' : ' — jen CEO')
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.notifyClientEvents}
              onChange={(e) => setForm({ ...form, notifyClientEvents: e.target.checked })}
            />
          }
          label="Posílat kopii všech e-mailů jeho klientům"
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Zrušit
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={submitting}>
          Založit distributora
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DistributorClientsDialog({
  distributor,
  onClose,
}: {
  distributor: Distributor
  onClose: () => void
}) {
  const api = useOpsApi()
  const [clients, setClients] = useState<DistributorClient[] | null>(null)
  const [options, setOptions] = useState<ClientSummary[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [selected, setSelected] = useState<ClientSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(() => {
    api
      .listDistributorClients(distributor.id)
      .then(setClients)
      .catch(() => setError('Klienty se nepodařilo načíst.'))
  }, [api, distributor.id])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    // Prázdný/krátký dotaz vrací z BE VŠECHNY klienty teritoria — nehledáme.
    if (searchInput.trim().length < 2) {
      setOptions([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      api
        .searchClients(searchInput)
        .then((found) => {
          if (!cancelled) setOptions(found)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [api, searchInput])

  const handleAssign = async () => {
    if (!selected) return
    try {
      await api.assignDistributorClient(distributor.id, selected.id)
      setSelected(null)
      reload()
    } catch {
      setError('Přiřazení se nepodařilo.')
    }
  }

  const handleUnassign = async (clientId: string) => {
    try {
      await api.unassignDistributorClient(distributor.id, clientId)
      reload()
    } catch {
      setError('Odebrání se nepodařilo.')
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Klienti — {distributor.name}{' '}
        <InfoHint
          lines={[
            'Přiřazení = klient dostane jmennou roli distributora (obsluha). Distributor ho uvidí ve svém přehledu a fee reportech.',
            'Klient má jediného distributora: má-li už jiného, přiřazením se mu ten předchozí odebere.',
            'Odebrání roli smaže — klient distributorovi zmizí; už spočítané fee běhy se zpětně nemění.',
          ]}
        />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Autocomplete
            size="small"
            sx={{ flexGrow: 1 }}
            options={options}
            value={selected}
            onChange={(_e, v) => setSelected(v)}
            inputValue={searchInput}
            onInputChange={(_e, v) => setSearchInput(v)}
            getOptionLabel={(o) => (o.personalNumber || o.companyId ? `${o.fullName} (${o.personalNumber ?? o.companyId})` : o.fullName)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            noOptionsText="Žádný klient nenalezen"
            renderInput={(params) => <TextField {...params} label="Přidat klienta (jméno, RČ, IČO)" />}
          />
          <Button variant="contained" onClick={handleAssign} disabled={!selected}>
            Přiřadit
          </Button>
        </Box>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {!clients ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : clients.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Zatím žádní klienti v obsluze.
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.clientId}>
                  <TableCell>{c.fullName}</TableCell>
                  <TableCell>{c.personalNumber ?? c.companyId ?? ''}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Odebrat z obsluhy">
                      <IconButton size="small" color="error" onClick={() => handleUnassign(c.clientId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zavřít</Button>
      </DialogActions>
    </Dialog>
  )
}


function EditDistributorDialog({
  distributor,
  onClose,
  onSaved,
}: {
  distributor: Distributor
  onClose: () => void
  onSaved: () => void
}) {
  const api = useOpsApi()
  const [name, setName] = useState(distributor.name)
  const [email, setEmail] = useState(distributor.email || '')
  // Sazba × 100 vyrábí plovoucí artefakty (0,29 → 28.999999999999996) a uložily
  // by se zpět do DB — zaokrouhlujeme na 4 desetinná místa procenta.
  const toPct = (rate: number) => String(Math.round(rate * 1e6) / 1e4)
  const [mfPct, setMfPct] = useState(
    distributor.mfCommissionRate != null ? toPct(distributor.mfCommissionRate) : ''
  )
  const [sfPct, setSfPct] = useState(
    distributor.sfCommissionRate != null ? toPct(distributor.sfCommissionRate) : ''
  )
  const [mode, setMode] = useState<CommissionMode>(distributor.commissionMode ?? 'FIXED')
  // Pásma jako text (procenta, Kč), převod až při uložení.
  const toRows = (tiers: CommissionTier[]) =>
    tiers.map((t) => ({ from: String(t.from), mf: toPct(t.mf), sf: toPct(t.sf) }))
  const [tiers, setTiers] = useState(toRows(distributor.commissionTiers ?? CONTRACT_TIERS))
  const setTier = (i: number, key: 'from' | 'mf' | 'sf', value: string) =>
    setTiers((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parsePct = (value: string): number | null | undefined => {
    if (!value.trim()) return null // smazat sazbu
    const n = Number(value.replace(',', '.'))
    if (Number.isNaN(n) || n < 0 || n > 100) return undefined // nevalidni
    return n / 100
  }

  const handleSave = async () => {
    const mf = parsePct(mfPct)
    const sf = parsePct(sfPct)
    if (mf === undefined || sf === undefined) {
      setError('Podíl zadejte v procentech 0–100.')
      return
    }
    let commissionTiers: CommissionTier[] | undefined
    if (mode === 'VOLUME') {
      commissionTiers = []
      for (const r of tiers) {
        const from = Number(r.from.replace(/\s/g, '').replace(',', '.'))
        const tmf = parsePct(r.mf)
        const tsf = parsePct(r.sf)
        if (!r.from.trim() || Number.isNaN(from) || from < 0 || tmf == null || tsf == null) {
          setError('Každé pásmo potřebuje hranici v Kč a oba podíly v procentech 0–100.')
          return
        }
        commissionTiers.push({ from, mf: tmf, sf: tsf })
      }
    }
    setSubmitting(true)
    setError(null)
    try {
      await api.updateDistributor(distributor.id, {
        name: name.trim() || undefined,
        email: email.trim(),
        mfCommissionRate: mf,
        sfCommissionRate: sf,
        commissionMode: mode,
        commissionTiers,
      })
      onSaved()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Uložení se nepodařilo.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth={mode === 'VOLUME' ? 'sm' : 'xs'} fullWidth>
      <DialogTitle>
        Upravit — {distributor.name}{' '}
        <InfoHint
          title="Podíly z fee"
          lines={[
            'Klientovi se strhne plná sazba dle třídy; distributor dostane z této částky svůj podíl.',
            'Podíl MF prázdný = žádná provize z management fee.',
            'Podíl SF prázdný = žádná provize ze success fee.',
            'Objemové podmínky: podíl podle souhrnu vkladů − výběrů všech klientů distributora. Pásmo platí na celý objem.',
            'MF se počítá po měsících — každý měsíc pásmo podle objemu ke konci předchozího měsíce. SF podle objemu k 31. 12.',
            'Změna platí pro DALŠÍ běhy — už spočítané se zpětně nemění.',
          ]}
        />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField size="small" label="Název" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField
          size="small"
          label="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          helperText="Víc adres oddělte čárkou. Login je první adresa."
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={mode}
          onChange={(_, v: CommissionMode | null) => v && setMode(v)}
        >
          <ToggleButton value="FIXED">Pevný podíl</ToggleButton>
          <ToggleButton value="VOLUME">Objemové podmínky</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'FIXED' ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              sx={{ flexGrow: 1 }}
              label="Podíl z MF (%)"
              placeholder="např. 50"
              value={mfPct}
              onChange={(e) => setMfPct(e.target.value)}
            />
            <TextField
              size="small"
              sx={{ flexGrow: 1 }}
              label="Podíl ze SF (%)"
              placeholder="např. 15"
              value={sfPct}
              onChange={(e) => setSfPct(e.target.value)}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {distributor.commissionMode === 'VOLUME' && distributor.volume != null && (
              <Typography variant="body2" color="text.secondary">
                Objem dnes: <b>{formatCzk(distributor.volume)}</b> → MF {fmtPct(distributor.currentMfRate)}, SF{' '}
                {fmtPct(distributor.currentSfRate)}
              </Typography>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Objem nad (Kč)</TableCell>
                  <TableCell>Podíl z MF (%)</TableCell>
                  <TableCell>Podíl ze SF (%)</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={t.from}
                        disabled={i === 0}
                        onChange={(e) => setTier(i, 'from', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" variant="standard" value={t.mf} onChange={(e) => setTier(i, 'mf', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" variant="standard" value={t.sf} onChange={(e) => setTier(i, 'sf', e.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      {i > 0 && (
                        <IconButton size="small" onClick={() => setTiers((rows) => rows.filter((_, j) => j !== i))}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setTiers((rows) => [...rows, { from: '', mf: '', sf: '' }])}>
                Přidat pásmo
              </Button>
              <Button size="small" onClick={() => setTiers(toRows(CONTRACT_TIERS))}>
                Pásma ze smlouvy
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Objem = vklady − výběry všech klientů distributora. Hranice patří ještě nižšímu pásmu („do 50 mil. včetně“).
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Zrušit
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Výchozí období: poslední ukončené čtvrtletí (yyyy-MM). */
function lastQuarter(): { from: string; to: string } {
  const now = new Date()
  const q = Math.floor(now.getMonth() / 3) // aktuální čtvrtletí 0..3
  const year = q === 0 ? now.getFullYear() - 1 : now.getFullYear()
  const startMonth = q === 0 ? 10 : (q - 1) * 3 + 1
  const pad = (m: number) => String(m).padStart(2, '0')
  return { from: `${year}-${pad(startMonth)}`, to: `${year}-${pad(startMonth + 2)}` }
}

/**
 * Souhrnné vyúčtování provize distributora z management fee za zvolené období (od–do po měsících).
 * Po strategiích, měsíce vedle sebe. Success fee má vlastní vyúčtování v Fee → Success fee.
 */
function DistributorStatementDialog({ distributor, onClose }: { distributor: Distributor; onClose: () => void }) {
  const api = useOpsApi()
  const initial = lastQuarter()
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [error, setError] = useState<string | null>(null)
  const thisMonth = new Date().toISOString().slice(0, 7)
  const invalid = !from || !to || from > to || to >= thisMonth

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Vyúčtování provize – {distributor.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Provize z management fee za zvolené období, po strategiích a měsících. Success fee se vyúčtovává
            zvlášť jednou ročně (Fee → Success fee).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              type="month"
              label="Od"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              size="small"
              type="month"
              label="Do"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          {invalid && (
            <Alert severity="info">Zvolte období, které už skončilo (Do nejpozději minulý měsíc, Od nejpozději Do).</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button variant="text" color="inherit" onClick={onClose}>
          Zavřít
        </Button>
        {!invalid && (
          <StatementButtons
            title={'Vyúčtování ' + distributor.name + ' ' + from + ' – ' + to}
            load={(format) => {
              setError(null)
              return api.distributorStatement(distributor.id, from, to, format)
            }}
            onError={setError}
          />
        )}
      </DialogActions>
    </Dialog>
  )
}

/** Pásma ze smlouvy o spolupráci (nové podmínky od 6/2026). */
const CONTRACT_TIERS: CommissionTier[] = [
  { from: 0, mf: 0.3, sf: 0.05 },
  { from: 50_000_000, mf: 0.35, sf: 0.1 },
  { from: 200_000_000, mf: 0.4, sf: 0.1 },
  { from: 500_000_000, mf: 0.5, sf: 0.15 },
]

function fmtPct(rate?: number | null): string {
  return rate != null ? Math.round(rate * 10000) / 100 + ' %' : '—'
}

function formatCzk(v: number): string {
  return v.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč'
}

/** Podíl v tabulce: pevný, nebo aktuální podíl z objemového pásma s objemem v tooltipu. */
function RateButton({ d, kind, onClick }: { d: Distributor; kind: 'mf' | 'sf'; onClick: () => void }) {
  if (d.commissionMode === 'VOLUME') {
    const rate = kind === 'mf' ? d.currentMfRate : d.currentSfRate
    return (
      <Tooltip title={'Objemové podmínky — objem ' + formatCzk(d.volume ?? 0) + '. Kliknutím upravíte pásma.'}>
        <span>
          <Button size="small" onClick={onClick} disabled={!d.roleId} sx={{ whiteSpace: 'nowrap' }}>
            {fmtPct(rate)}
            <Chip size="small" label="objem" sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
          </Button>
        </span>
      </Tooltip>
    )
  }
  const rate = kind === 'mf' ? d.mfCommissionRate : d.sfCommissionRate
  return (
    <Tooltip title="Kliknutím upravíte podíl">
      <span>
        <Button size="small" onClick={onClick} disabled={!d.roleId}>
          {rate != null ? fmtPct(rate) : 'nastavit'}
        </Button>
      </span>
    </Tooltip>
  )
}
